import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Loader2, RefreshCw, Send, ShieldCheck, Wallet } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { useToast } from 'components/ui/use-toast';
import { cn } from 'lib/utils';
import { Profile } from 'modules/auth/api';
import { createPortalTopUpCheckout, getPortalPaymentDetails, getPortalPaymentMethods } from 'modules/portal-plans/api';
import { calculateTestPayment } from 'modules/portal-plans/testPayment';
import {
  PAYMENT_SUPPORT_TELEGRAM,
  isOnlineMethod,
  pickPaymentMethod,
  type OnlinePaymentMethod,
  type PaymentMethod,
} from 'modules/payments/methods';
import { PaymentMethodMark } from 'components/payments/PaymentMethodMark';
import { portalDialogCloseClass, portalDialogFitClass } from 'components/portal/upsell';

const som = (value: number) => `${value.toLocaleString('uz-UZ')} so‘m`;

const METHOD_NAMES: Record<OnlinePaymentMethod, string> = {
  payme: 'Payme', click: 'Click', uzum: 'Uzum', uzcard: 'Uzcard', humo: 'Humo', visa_mc: 'Visa / Mastercard',
};

export interface TestPaymentTest {
  id: string;
  title: string;
  priceSom: number;
  questionsCount?: number;
  durationMinutes?: number;
  sectionsCount?: number;
}

interface TestPaymentDialogProps {
  test: TestPaymentTest;
  balanceSom: number;
  purchasing: boolean;
  onConfirm: () => void;
  /** Receives the balance the server reported when it rejected a stale amount. */
  onBalanceChanged: (balanceSom: number) => void;
  onClose: () => void;
}

export function TestPaymentDialog({ test, balanceSom, purchasing, onConfirm, onBalanceChanged, onClose }: TestPaymentDialogProps) {
  const { t } = useTranslation();
  const { priceSom, fromBalanceSom, dueSom, balanceAfterSom } = calculateTestPayment(test.priceSom, balanceSom);
  const needsPayment = dueSom > 0;
  const [chosenMethod, setMethod] = useState<PaymentMethod | null>(null);
  const methods = useQuery({
    queryKey: ['portal-payment-methods'],
    queryFn: ({ signal }) => getPortalPaymentMethods(signal),
    enabled: needsPayment,
    staleTime: 60_000,
  });
  const offered = methods.data ?? [];
  const method = pickPaymentMethod(offered, chosenMethod);
  const balanceNowSom = fromBalanceSom + balanceAfterSom;
  // Whatever the caller does not know (questions, minutes, sections) is left out of the line.
  const facts = [
    test.questionsCount ? t('portal_pay_fact_questions', { count: test.questionsCount }) : null,
    test.durationMinutes ? t('portal_pay_fact_minutes', { count: test.durationMinutes }) : null,
    test.sectionsCount ? t('portal_pay_fact_sections', { count: test.sectionsCount }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const selectMethod = (step: number) => {
    if (!method || offered.length === 0) return;
    const next = offered[(offered.indexOf(method) + step + offered.length) % offered.length];
    setMethod(next);
    document.getElementById(`test-payment-tab-${next}`)?.focus();
  };

  return (
    <Dialog open onOpenChange={open => { if (!open && !purchasing) onClose(); }}>
      <DialogContent closeClassName={portalDialogCloseClass}
          className={cn(portalDialogFitClass, 'max-w-md')}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold tracking-tight">
            {needsPayment ? t('portal_pay_title') : t('portal_pay_start_title')}
          </DialogTitle>
          <DialogDescription className={cn('text-sm', !needsPayment && 'font-semibold text-foreground')}>
            {test.title}
          </DialogDescription>
          {!needsPayment && facts && <p className="text-xs text-muted-foreground">{facts}</p>}
        </DialogHeader>

        {needsPayment ? (
          <div className="rounded-xl border border-border bg-muted/40 p-4 text-sm">
            <dl className="space-y-2.5">
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">{t('portal_pay_price')}</dt>
                <dd className="font-semibold tabular-nums text-foreground">{som(priceSom)}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="flex items-center gap-1.5 text-muted-foreground">
                  <Wallet aria-hidden="true" className="h-4 w-4 shrink-0" />
                  {t('portal_pay_balance')}
                </dt>
                <dd className="font-semibold tabular-nums text-foreground">{som(balanceNowSom)}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-3 border-t border-dashed border-border pt-3">
                <dt className="font-semibold text-foreground">{t('portal_pay_short')}</dt>
                <dd className="font-display text-[22px] font-extrabold tabular-nums text-amber-700 dark:text-amber-400">
                  {som(dueSom)}
                </dd>
              </div>
            </dl>
            <p className="mt-0.5 text-xs text-muted-foreground">{t('portal_pay_short_hint')}</p>
          </div>
        ) : (
          <dl className="space-y-2.5 rounded-xl border border-border bg-muted/40 p-4 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-muted-foreground">{t('portal_pay_your_balance')}</dt>
              <dd className="font-semibold tabular-nums text-foreground">{som(balanceNowSom)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-muted-foreground">{t('portal_pay_price')}</dt>
              <dd className="font-semibold tabular-nums text-foreground">−{som(priceSom)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-3 border-t border-border pt-3">
              <dt className="font-bold text-foreground">{t('portal_pay_left')}</dt>
              <dd className="font-display text-[22px] font-extrabold tabular-nums text-foreground">{som(balanceAfterSom)}</dd>
            </div>
          </dl>
        )}

        {needsPayment ? (
          <>
            <TopUpSteps dueSom={dueSom} priceSom={priceSom} />

            <div className="space-y-2.5">
              <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('portal_pay_method_label')}
              </span>
              {methods.isLoading ? (
                <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : methods.isError ? (
                <div className="flex items-center gap-3">
                  <p role="alert" className="text-sm text-foreground">{t('portal_pay_methods_error')}</p>
                  <Button variant="outline" size="sm" onClick={() => void methods.refetch()}>{t('retry')}</Button>
                </div>
              ) : !method ? (
                <p role="alert" className="text-sm text-muted-foreground">{t('portal_pay_methods_none')}</p>
              ) : (
                <>
                  <div
                    role="tablist"
                    aria-label={t('portal_pay_method_aria')}
                    className={cn(
                      'grid gap-1 rounded-xl border border-border/60 bg-muted/60 p-1',
                      offered.length === 1 ? 'grid-cols-1' : offered.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3',
                    )}
                    onKeyDown={event => {
                      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                      event.preventDefault();
                      selectMethod(event.key === 'ArrowLeft' ? -1 : 1);
                    }}
                  >
                    {offered.map(id => (
                      <MethodTab key={id} method={id} selected={method === id} onSelect={setMethod}>
                        <PaymentMethodMark method={id} cardLabel={t('portal_pay_method_card')} />
                      </MethodTab>
                    ))}
                  </div>

                  {isOnlineMethod(method) ? (
                    <OnlineTopUpPanel key={method} method={method} testId={test.id} dueSom={dueSom} onBalanceChanged={onBalanceChanged} />
                  ) : (
                    <CardTransferPanel title={test.title} dueSom={dueSom} onBalanceChanged={onBalanceChanged} />
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">{t('portal_pay_starts_now')}</p>
        )}

        <DialogFooter className="mt-1 gap-2">
          <Button variant="outline" onClick={onClose} disabled={purchasing}>
            {needsPayment ? t('close') : t('cancel')}
          </Button>
          {!needsPayment && (
            <Button className="h-11 font-bold" onClick={onConfirm} disabled={purchasing}>
              {purchasing ? t('portal_pay_purchasing') : t('portal_pay_confirm_charge', { price: som(priceSom) })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The two-step flow in plain words: top up first, then the test price comes off the balance. */
function TopUpSteps({ dueSom, priceSom }: { dueSom: number; priceSom: number }) {
  const { t } = useTranslation();
  const steps = [
    { title: t('portal_pay_step1_title'), text: t('portal_pay_step1_text', { amount: som(dueSom) }) },
    { title: t('portal_pay_step2_title'), text: t('portal_pay_step2_text') },
    { title: t('portal_pay_step3_title'), text: t('portal_pay_step3_text', { price: som(priceSom) }) },
  ];

  return (
    <div className="space-y-2.5">
      <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t('portal_pay_steps_title')}
      </span>
      <ol>
        {steps.map((step, index) => {
          const current = index === 0;
          const last = index === steps.length - 1;
          return (
            <li key={step.title} className="flex gap-3">
              <div className="flex flex-col items-center">
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-[1.5px] text-[13px] font-bold',
                    current
                      ? 'border-foreground bg-primary text-primary-foreground'
                      : 'border-border bg-background text-muted-foreground',
                  )}
                >
                  {index + 1}
                </span>
                {!last && <span aria-hidden="true" className="my-1 w-0.5 grow bg-border" />}
              </div>
              <div className={cn('flex flex-col gap-0.5', !last && 'pb-3')}>
                <span className={cn('text-sm', current ? 'font-bold text-foreground' : 'font-semibold text-foreground/80')}>
                  {step.title}
                </span>
                <span className="text-[13px] text-muted-foreground">{step.text}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

interface MethodTabProps {
  method: PaymentMethod;
  selected: boolean;
  onSelect: (method: PaymentMethod) => void;
  children: React.ReactNode;
}

function MethodTab({ method, selected, onSelect, children }: MethodTabProps) {
  return (
    <button
      type="button"
      role="tab"
      id={`test-payment-tab-${method}`}
      aria-selected={selected}
      aria-controls={`test-payment-panel-${method}`}
      tabIndex={selected ? 0 : -1}
      onClick={() => onSelect(method)}
      className={cn(
        'flex h-10 items-center justify-center gap-2 rounded-lg px-3 text-[13px] outline-none transition-[color,background-color,box-shadow] duration-200 focus-visible:ring-2 focus-visible:ring-ring',
        selected
          ? 'bg-primary font-bold text-primary-foreground shadow-md'
          : 'font-medium text-muted-foreground hover:bg-background/40 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

interface OnlineTopUpPanelProps {
  method: OnlinePaymentMethod;
  testId: string;
  dueSom: number;
  onBalanceChanged: (balanceSom: number) => void;
}

/** The provider's page credits the amount due to the balance; the test is then bought from balance. */
function OnlineTopUpPanel({ method, testId, dueSom, onBalanceChanged }: OnlineTopUpPanelProps) {
  const { t } = useTranslation();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const checkoutRequest = useRef<AbortController | null>(null);

  useEffect(() => () => checkoutRequest.current?.abort(), []);

  const pay = async () => {
    if (checkoutRequest.current) return;
    const controller = new AbortController();
    checkoutRequest.current = controller;
    setPaying(true);
    setError('');
    try {
      const checkout = await createPortalTopUpCheckout(testId, dueSom, method, controller.signal);
      if (!controller.signal.aborted) window.location.assign(checkout.checkoutUrl);
    } catch (err: unknown) {
      if (controller.signal.aborted) return;
      const data = (err as { response?: { data?: { message?: unknown; balanceSom?: unknown } } }).response?.data;
      if (typeof data?.balanceSom === 'number') onBalanceChanged(data.balanceSom);
      setError(typeof data?.message === 'string' ? data.message : t('portal_pay_online_error'));
    } finally {
      if (checkoutRequest.current === controller) {
        checkoutRequest.current = null;
        setPaying(false);
      }
    }
  };

  return (
    <div role="tabpanel" id={`test-payment-panel-${method}`} aria-labelledby={`test-payment-tab-${method}`} className="space-y-3">
      {error && (
        <p role="alert" className="rounded-lg bg-destructive/10 p-2 text-xs font-semibold text-destructive">
          {error}
        </p>
      )}
      <Button
        type="button"
        onClick={() => void pay()}
        disabled={paying}
        aria-busy={paying}
        className="h-12 w-full gap-2 bg-foreground text-sm font-bold text-background hover:bg-foreground/90 motion-safe:active:scale-[0.96]"
      >
        <span>
          {paying
            ? t('portal_pay_online_opening')
            : t('portal_pay_online_button', { method: METHOD_NAMES[method], amount: som(dueSom) })}
        </span>
      </Button>
      <p className="flex gap-2 text-xs leading-relaxed text-muted-foreground">
        <ShieldCheck aria-hidden="true" className="mt-px h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <span>{t('portal_pay_online_note')}</span>
      </p>
    </div>
  );
}

type BalanceCheck = 'idle' | 'checking' | 'pending' | 'error';

interface CardTransferPanelProps {
  title: string;
  dueSom: number;
  onBalanceChanged: (balanceSom: number) => void;
}

function CardTransferPanel({ title, dueSom, onBalanceChanged }: CardTransferPanelProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [copied, setCopied] = useState<'card' | 'amount' | null>(null);
  const [balanceCheck, setBalanceCheck] = useState<BalanceCheck>('idle');
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const details = useQuery({
    queryKey: ['portal-payment-details'],
    queryFn: ({ signal }) => getPortalPaymentDetails(signal),
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(null), 2500);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async (field: 'card' | 'amount', value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(field);
    } catch {
      toast.error(t('portal_pay_copy_failed'), { description: t('portal_pay_copy_failed_hint') });
    }
  };

  /** A card transfer is confirmed by hand, so the learner re-reads the balance instead of reopening the test. */
  const checkBalance = async () => {
    if (balanceCheck === 'checking') return;
    setBalanceCheck('checking');
    try {
      const { data } = await Profile();
      if (!mounted.current) return;
      const next = data.user?.balanceSom;
      if (typeof next === 'number') onBalanceChanged(next);
      setBalanceCheck('pending');
    } catch {
      if (mounted.current) setBalanceCheck('error');
    }
  };

  const telegramUrl = `${PAYMENT_SUPPORT_TELEGRAM}?text=${encodeURIComponent(
    `Assalomu alaykum! Examing'da "${title}" testi uchun ${som(dueSom)} to'lov qildim. Chekni ilova qilaman.`,
  )}`;

  return (
    <div role="tabpanel" id="test-payment-panel-card" aria-labelledby="test-payment-tab-card" className="space-y-4">
      {details.isLoading ? (
        <p role="status" className="py-4 text-center text-sm text-muted-foreground">
          {t('portal_pay_card_loading')}
        </p>
      ) : details.isError ? (
        <div className="space-y-2 py-2 text-center">
          <p role="alert" className="text-sm text-foreground">{t('portal_pay_card_load_error')}</p>
          <Button variant="outline" onClick={() => void details.refetch()}>{t('retry')}</Button>
        </div>
      ) : !details.data ? (
        <p role="alert" className="py-4 text-center text-sm text-muted-foreground">
          {t('portal_pay_card_unavailable')}
        </p>
      ) : (
        <>
          <div className="rounded-xl bg-neutral-900 p-4 text-white">
            <span className="block text-[11px] font-semibold uppercase tracking-widest text-neutral-400">
              {t('portal_pay_card_number')}
            </span>
            <div className="mt-1 flex items-center justify-between gap-2">
              <span className="font-mono text-lg font-bold tracking-widest">
                {details.data.cardNumber.replace(/(\d{4})(?=\d)/g, '$1 ')}
              </span>
              <button
                type="button"
                onClick={() => void copy('card', details.data!.cardNumber)}
                aria-label={t('portal_pay_copy_card')}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white/15 transition-colors hover:bg-white/25 motion-safe:active:scale-[0.96]"
              >
                {copied === 'card' ? <Check className="h-[18px] w-[18px] text-emerald-400" /> : <Copy className="h-[18px] w-[18px]" />}
              </button>
            </div>
            <span className="mt-1 block truncate text-xs font-semibold uppercase tracking-wide text-neutral-300">
              {details.data.cardHolderName}
            </span>
          </div>

          <ol className="space-y-3 text-sm">
            <li className="flex gap-3">
              <StepNumber>1</StepNumber>
              <div className="flex min-w-0 grow flex-col gap-1.5">
                <span>{t('portal_pay_card_step1')}</span>
                <div className="flex items-center gap-2">
                  <strong className="font-display text-base font-extrabold tabular-nums text-foreground">{som(dueSom)}</strong>
                  <button
                    type="button"
                    onClick={() => void copy('amount', String(dueSom))}
                    aria-label={t('portal_pay_copy_amount')}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    {copied === 'amount' ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </li>
            <li className="flex gap-3">
              <StepNumber>2</StepNumber>
              <span>{t('portal_pay_card_step2')}</span>
            </li>
            <li className="flex gap-3">
              <StepNumber>3</StepNumber>
              <span>{t('portal_pay_card_step3')}</span>
            </li>
          </ol>

          <div className="space-y-2">
            <Button
              asChild
              className="h-12 w-full bg-[#1a7fb3] text-sm font-bold text-white hover:bg-[#166a96]"
            >
              <a href={telegramUrl} target="_blank" rel="noopener noreferrer">
                <Send aria-hidden="true" className="mr-2 h-4 w-4" />
                {t('portal_pay_telegram')}
              </a>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => void checkBalance()}
              disabled={balanceCheck === 'checking'}
              aria-busy={balanceCheck === 'checking'}
              className="h-11 w-full gap-2 font-semibold"
            >
              <RefreshCw aria-hidden="true" className={cn('h-4 w-4', balanceCheck === 'checking' && 'motion-safe:animate-spin')} />
              {balanceCheck === 'checking' ? t('portal_pay_checking') : t('portal_pay_check_balance')}
            </Button>
            {balanceCheck === 'pending' && (
              <p role="status" className="text-center text-xs text-muted-foreground">{t('portal_pay_check_pending')}</p>
            )}
            {balanceCheck === 'error' && (
              <p role="alert" className="text-center text-xs font-semibold text-destructive">{t('portal_pay_check_error')}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StepNumber({ children }: { children: React.ReactNode }) {
  return (
    <span
      aria-hidden="true"
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-foreground text-xs font-bold text-background"
    >
      {children}
    </span>
  );
}

export default TestPaymentDialog;
